/** Direct browser measurement, independent of GTM and Google Ads configuration. */
export const OPENAI_PIXEL_ID = "Kvw73wzCJaSPAEksSy75op";
export const OPENAI_PIXEL_SCRIPT = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
export const MEASUREMENT_CONSENT_EVENT = "thiscar:measurement-consent";

type PixelQueue = ((...args: unknown[]) => void) & { q?: unknown[][] };
type PixelWindow = Window & {
    oaiq?: PixelQueue;
    thiscarOpenaiPixelInitialized?: boolean;
    thiscarOpenaiLeadEvents?: Set<string>;
};

export function measurementAllowed(): boolean {
    try { return typeof window !== "undefined" && window.localStorage.getItem("cookie-consent") === "true"; }
    catch { return false; }
}

export function initializeOpenaiPixel(): PixelQueue | undefined {
    if (typeof window === "undefined") return undefined;
    const target = window as PixelWindow;
    if (!target.oaiq) {
        const queue: PixelQueue = (...args) => { queue.q!.push(args); };
        queue.q = [];
        target.oaiq = queue;
    }
    const pixel = target.oaiq;
    if (!target.thiscarOpenaiPixelInitialized) {
        pixel("consent", measurementAllowed());
        pixel("init", { pixelId: OPENAI_PIXEL_ID });
        target.thiscarOpenaiPixelInitialized = true;
        if (!document.querySelector(`script[src="${OPENAI_PIXEL_SCRIPT}"]`)) {
            const script = document.createElement("script");
            script.async = true;
            script.src = OPENAI_PIXEL_SCRIPT;
            document.head.appendChild(script);
        }
    }
    return pixel;
}

export function syncOpenaiConsent(): void {
    try { initializeOpenaiPixel()?.("consent", measurementAllowed()); }
    catch { /* Measurement must never interrupt the customer flow. */ }
}

/** Call only after the server confirms acceptance; never on click or form submit. */
export function trackOpenaiLeadCreated(eventId: string | undefined): void {
    try {
        // Never manufacture a fresh ID when the same accepted result is rendered again.
        if (!eventId || !measurementAllowed()) return;
        const pixel = initializeOpenaiPixel();
        if (!pixel) return;
        const target = window as PixelWindow;
        const sent = target.thiscarOpenaiLeadEvents ??= new Set<string>();
        if (sent.has(eventId)) return;
        const key = `thiscar:openai:lead:${eventId}`;
        try { if (window.sessionStorage.getItem(key)) return; } catch { /* Storage may be disabled. */ }
        // Mark before calling the SDK so a synchronous callback cannot send the same receipt twice.
        sent.add(eventId);
        try {
            pixel("consent", true);
            pixel("measureSingle", OPENAI_PIXEL_ID, "lead_created", { type: "customer_action" }, { event_id: eventId });
        } catch (error) {
            sent.delete(eventId);
            throw error;
        }
        try { window.sessionStorage.setItem(key, "true"); } catch { /* SDK event_id also deduplicates. */ }
    } catch { /* Analytics must never prevent an accepted inquiry from completing. */ }
}
